// Makes the whole CEF-rendered UI track the game's actual render resolution,
// using the SAME formula skyrim-platform's native cursor sprite already
// scales by (DX11RenderHandler.cpp's OnRender: `m_height / 1080.f`, height
// rather than width specifically to avoid ballooning on ultrawide monitors -
// see that file's own comment). Without this, the web UI stays at a fixed
// 1:1 CSS-pixel-to-physical-pixel size regardless of resolution (CEF's
// GetViewRect reports the real swap-chain size with no device_scale_factor
// applied - confirmed in DX11RenderHandler.cpp/MyChromiumApp.cpp), so the
// whole UI visually SHRINKS at 4K/8K relative to the screen while the cursor
// sprite GROWS by that same height/1080 factor on purpose - the exact
// mismatch behind "the mouse looks giant" at high resolutions. Matching the
// UI's own scale to the cursor's formula makes both grow together again,
// restoring the same relative proportions at every resolution.
//
// Applied via `zoom` (not a `--u`-style calc() scaling unit threaded through
// every component's own px values, escMenu/styles.scss's own partial
// attempt at that) - CEF is the only rendering engine this UI ever runs in,
// and `zoom` scales an entire subtree (layout AND hit-testing, unlike
// `transform: scale` which needs extra work to keep clicks landing in the
// right place) with zero changes to any existing component's CSS.
//
// Getting the reference height right has already broken twice, each fix
// exposing the next wrong assumption - both mistakes are left documented
// below (applyZoom/getPhysicalHeight) since a THIRD attempt at "just read
// some browser API for the real resolution" would very likely repeat one of
// them:
//   1. `window.innerHeight` - the CSS layout viewport, which `zoom` itself
//      changes. Applying zoom=2 on a 4K screen immediately halved the
//      reported innerHeight back to ~1080, so the resize listener recomputed
//      autoScale as 1080/1080=1 and zoomed straight back out again a moment
//      later. Symptom: the UI stayed at its old unscaled 1:1 size on 4K.
//   2. `window.screen.height` - the desktop's native monitor resolution,
//      immune to our own zoom, but NOT the same thing as this browser
//      surface's actual render size the moment the game runs windowed or at
//      a non-native resolution (e.g. a 4K monitor with the game's own video
//      settings set to 1440p, or a maximized-but-not-fullscreen window).
//      `screen.height` stayed pinned to the monitor's full native height
//      regardless, over-scaling the UI relative to the actually-smaller
//      surface it was drawn into. Symptom: "hyper zoomé" on 2K/4K displays.
const REFERENCE_HEIGHT = 1080;
const USER_SCALE_STORAGE_KEY = 'skymp5-esc-menu-ui-scale';
export const DEFAULT_USER_SCALE = 1;
export const MIN_USER_SCALE = 0.5;
export const MAX_USER_SCALE = 2;

export const getStoredUserScale = (): number => {
  try {
    const raw = window.localStorage.getItem(USER_SCALE_STORAGE_KEY);
    const parsed = raw ? parseFloat(raw) : DEFAULT_USER_SCALE;
    if (!Number.isFinite(parsed)) return DEFAULT_USER_SCALE;
    return Math.max(MIN_USER_SCALE, Math.min(MAX_USER_SCALE, parsed));
  } catch {
    return DEFAULT_USER_SCALE;
  }
};

// Measures the TRUE physical render-surface height and applies the right
// zoom for it, in one atomic step - rather than trying to reconstruct that
// physical height from a live `innerHeight` reading while some previous
// zoom is still in effect (which depends on exactly how a given Chromium/
// CEF embedding recomputes the layout viewport under zoom, an assumption
// that already broke once - see this file's top comment). Resetting zoom to
// 1 first makes the reading unconditionally honest: at zoom=1, CSS pixels
// equal physical pixels by definition, no embedding-specific behavior to
// get wrong. The same function runs for the initial call, every resize, and
// every user-scale change, so there's no separate "first measurement" to
// drift out of sync with later ones.
function measureAndApplyZoom(userScale: number): void {
  const style = document.documentElement.style as unknown as { zoom: string };
  style.zoom = '1';
  const physicalHeight = window.innerHeight || REFERENCE_HEIGHT;
  const zoom = (physicalHeight / REFERENCE_HEIGHT) * userScale;
  style.zoom = String(zoom);
  // Exposed so a panel that does its OWN independent vw-based scaling (escMenu's
  // `--u`, see its styles.scss) can cancel this zoom back out with `zoom: calc(1
  // / var(--applied-zoom))` instead of compounding with it - two multiplicative
  // scale systems stacking is what made the esc menu balloon off-screen at 4K.
  document.documentElement.style.setProperty('--applied-zoom', String(zoom));
}

// Called once at bootstrap (index.js), before initPalette-style callers, and
// again on resize (a display-mode/resolution change while running - the
// native side re-fires WasResized() on its own render handler, so the web
// side needs its own listener to stay in sync rather than assuming the
// value it read at load time is permanent).
export const initUiScale = (): void => {
  measureAndApplyZoom(getStoredUserScale());
  window.addEventListener('resize', () => measureAndApplyZoom(getStoredUserScale()));
};

// Sets and persists the player's own multiplier on top of the automatic
// resolution-based scale - e.g. someone who finds even the resolution-
// corrected size too large/small for their taste. 1 = no extra adjustment.
export const applyUserScale = (userScale: number): void => {
  const clamped = Math.max(MIN_USER_SCALE, Math.min(MAX_USER_SCALE, userScale));
  measureAndApplyZoom(clamped);
  try {
    window.localStorage.setItem(USER_SCALE_STORAGE_KEY, String(clamped));
  } catch {
    /* ignore */
  }
};

// Ctrl+wheel step, used by the global zoom shortcut in index.js - works from
// anywhere, including while a panel is too big to reach its own options tab
// (the whole point: it's the escape hatch for that case, not just a
// convenience once you're already in the menu).
const USER_SCALE_STEP = 0.1;
export const stepUserScale = (direction: 1 | -1): void => {
  applyUserScale(getStoredUserScale() + direction * USER_SCALE_STEP);
};
